import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useToast } from "../../context/ToastContext";
import { getNeedsAttention } from "../../api/needsAttentionAPI";
import CommentsBanner from "./CommentsBanner";
import PhaseColumn from "./PhaseColumn";
import BucketSection from "./BucketSection";

const SkeletonRow = () => (
  <div className="flex items-center gap-3 py-2.5 border-b border-gray-100 last:border-b-0">
    <div className="h-8 w-8 rounded-full bg-gray-200" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 bg-gray-200 rounded w-2/3" />
      <div className="h-2.5 bg-gray-100 rounded w-1/3" />
    </div>
    <div className="h-6 w-16 bg-gray-100 rounded-full" />
  </div>
);

const SkeletonCard = ({ rows = 3 }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4 animate-pulse">
    <div className="flex items-center justify-between mb-3">
      <div className="h-3.5 bg-gray-200 rounded w-40" />
      <div className="h-5 w-14 bg-gray-100 rounded-full" />
    </div>
    <div className="rounded-lg border border-gray-100 px-3">
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonRow key={i} />
      ))}
    </div>
  </div>
);

const NeedsAttentionSkeleton = () => (
  <div className="p-2 grid grid-cols-1 lg:grid-cols-2 gap-5">
    <SkeletonCard rows={3} />
    <SkeletonCard rows={2} />
    <SkeletonCard rows={2} />
    <SkeletonCard rows={3} />
  </div>
);

const EmptyState = () => (
  <div className="py-20 text-center flex flex-col items-center">
    <div className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center mb-5">
      <svg
        className="w-12 h-12 text-emerald-500"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </div>
    <div className="text-base font-semibold text-gray-800 mb-1">
      You're all caught up
    </div>
    <div className="text-sm text-gray-500 max-w-md">
      Nothing needs attention right now. Every household is on track — check back
      later or hop over to{" "}
      <span className="font-medium text-gray-700">All Households</span> to keep
      working.
    </div>
  </div>
);

const NeedsAttentionView = ({ projectId, onTotalsLoaded, actions, refreshKey }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalKey, setInternalKey] = useState(0);

  const refresh = () => setInternalKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getNeedsAttention(projectId)
      .then((payload) => {
        if (cancelled) return;
        setData(payload);
        if (typeof onTotalsLoaded === "function") {
          onTotalsLoaded(payload?.total_flagged || 0);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        toast.fromError(err, "We could not load the Needs Attention view.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, internalKey, refreshKey]);

  if (loading) {
    return <NeedsAttentionSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="py-16 text-center text-sm text-gray-500">
        Could not load the Needs Attention view.
      </div>
    );
  }

  const totalFlagged = data.total_flagged || 0;

  if (totalFlagged === 0) {
    return <EmptyState />;
  }

  if (data.flow === "phase") {
    const phases = data.phases || [];
    const gridClass =
      phases.length >= 2
        ? "grid grid-cols-1 lg:grid-cols-2 gap-5"
        : "grid grid-cols-1 gap-5";
    return (
      <div className="p-2">
        <CommentsBanner
          banner={data.comments_banner}
          projectId={projectId}
          navigate={navigate}
        />
        {phases.length > 0 && (
          <div className={gridClass}>
            {phases.map((phase, i) => (
              <PhaseColumn
                key={phase.key}
                phase={phase}
                phaseNumber={i + 1}
                projectId={projectId}
                navigate={navigate}
                actions={actions}
                onActionComplete={refresh}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const buckets = data.buckets || [];

  return (
    <div className="p-2">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-5 px-1">
        <div className="text-sm text-gray-700">
          <span className="text-lg font-bold text-gray-900">{totalFlagged}</span>{" "}
          household{totalFlagged === 1 ? "" : "s"} need attention
        </div>
        {data.open_nigo_count > 0 && (
          <div className="text-sm text-gray-500">
            <span className="font-semibold text-red-600">
              {data.open_nigo_count}
            </span>{" "}
            open NIGO{data.open_nigo_count === 1 ? "" : "s"}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {buckets.map((bucket) => (
          <BucketSection
            key={bucket.key}
            bucket={bucket}
            projectId={projectId}
            navigate={navigate}
            actions={actions}
            onActionComplete={refresh}
          />
        ))}
      </div>
    </div>
  );
};

export default NeedsAttentionView;
